/**
 * LOGOS v5.1 — EmergenceReporter (EMA Component C)
 * 
 * Captures emergence events as part of the daily check-in flow.
 * Designed for ZERO FRICTION — adds only 15-45 seconds to daily EMA.
 * 
 * Flow:
 * 0. (First time) Show education step explaining what emergence events are
 * 1. Show 6 emergence categories (E1-E6) as tappable cards
 * 2. If user taps a category → show impact slider (1-10) + optional description
 * 3. User can also tap "skip" (no emergence)
 * 4. On submit → POST to emergence_events with full state_snapshot
 * 5. Show celebration with confetti, motivational message, event counter
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EMERGENCE_TAXONOMY, computeERS } from '../core/protocol-engine';

const COLORS = {
  bg: '#0F0F1A',
  card: '#1A1A2E',
  cardHover: '#252540',
  border: '#2D2D44',
  text: '#E0E0F0',
  textDim: '#8888AA',
  logos: '#C084FC',
  accent: '#06B6D4',
  success: '#10B981',
  warning: '#F59E0B',
  gold: '#FBBF24',
};

// ─── CSS confetti animation (pure CSS, no library) ───
const confettiKeyframes = `
@keyframes confetti-fall {
  0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(200px) rotate(720deg); opacity: 0; }
}
@keyframes celebration-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}
@keyframes celebration-glow {
  0%, 100% { box-shadow: 0 0 20px rgba(192,132,252,0.3); }
  50% { box-shadow: 0 0 40px rgba(192,132,252,0.6), 0 0 80px rgba(251,191,36,0.3); }
}
`;

const ConfettiParticles = () => {
  const particles = Array.from({ length: 24 }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 2}s`,
    duration: `${1.5 + Math.random() * 2}s`,
    color: ['#C084FC', '#FBBF24', '#10B981', '#06B6D4', '#F59E0B', '#EC4899'][i % 6],
    size: 4 + Math.random() * 4,
  }));
  return (
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100%', overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', top: -10, left: p.left,
          width: p.size, height: p.size, borderRadius: p.size > 6 ? '50%' : '1px',
          background: p.color,
          animation: `confetti-fall ${p.duration} ease-out ${p.delay} forwards`,
        }} />
      ))}
    </div>
  );
};

export default function EmergenceReporter({
  onSubmit,    // (category, impact, description, goalAssociation, latencyS) => void
  onSkip,      // (latencyS) => void — no emergence today
  onClose,     // () => void — close modal without action
  currentState, // { lambda, viability, coherence, entropy, ...28dims }
  visible = false,
}) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [impact, setImpact] = useState(5);
  const [description, setDescription] = useState('');
  const [goalAssociation, setGoalAssociation] = useState('');
  const [step, setStep] = useState('select'); // 'education' | 'select' | 'detail' | 'celebration' | 'done'
  const [eventCount, setEventCount] = useState(0);
  const startTime = useRef(Date.now());

  useEffect(() => {
    if (visible) {
      startTime.current = Date.now();
      setSelectedCategory(null);
      setImpact(5);
      setDescription('');
      setGoalAssociation('');
      // Check if first time — show education
      const seen = localStorage.getItem('logos-emergence-seen');
      const count = parseInt(localStorage.getItem('logos-emergence-count') || '0', 10);
      setEventCount(count);
      setStep(seen ? 'select' : 'education');
    }
  }, [visible]);

  if (!visible) return null;

  const ers = computeERS(
    currentState?.lambda || 0.5,
    currentState?.viability || 0.5,
    currentState?.coherence || 0.5,
    currentState?.entropy || 0.5
  );

  const handleEducationDone = () => {
    try { localStorage.setItem('logos-emergence-seen', 'true'); } catch {}
    setStep('select');
  };

  const handleCategorySelect = (catKey) => {
    setSelectedCategory(catKey);
    setStep('detail');
  };

  const handleSubmit = () => {
    const latency = Math.round((Date.now() - startTime.current) / 1000);
    onSubmit(selectedCategory, impact, description, goalAssociation, latency);
    // Increment event counter
    const newCount = eventCount + 1;
    setEventCount(newCount);
    try { localStorage.setItem('logos-emergence-count', String(newCount)); } catch {}
    setStep('celebration');
  };

  const handleSkip = () => {
    const latency = Math.round((Date.now() - startTime.current) / 1000);
    onSkip(latency);
    setStep('done');
  };

  const categories = Object.entries(EMERGENCE_TAXONOMY);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px',
    }}>
      <style>{confettiKeyframes}</style>
      <div style={{
        background: COLORS.bg, borderRadius: '16px', border: `1px solid ${COLORS.border}`,
        maxWidth: '480px', width: '100%', maxHeight: '90vh', overflow: 'auto',
        padding: '24px', position: 'relative',
        ...(step === 'celebration' ? { animation: 'celebration-glow 2s ease-in-out infinite' } : {}),
      }}>

        {/* ═══ EDUCATION STEP (first time only) ═══ */}
        {step === 'education' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>✨</div>
            <h2 style={{ color: COLORS.gold, fontSize: '18px', margin: 0, fontWeight: 600, marginBottom: '12px' }}>
              {t('emergence.education.title')}
            </h2>
            <p style={{ color: COLORS.textDim, fontSize: '13px', lineHeight: 1.5, marginBottom: '16px' }}>
              {t('emergence.education.body')}
            </p>
            <div style={{ textAlign: 'left', marginBottom: '16px' }}>
              {(t('emergence.education.items', { returnObjects: true }) || []).map((item, i) => (
                <div key={i} style={{ color: COLORS.text, fontSize: '13px', padding: '6px 0', lineHeight: 1.4 }}>
                  {item}
                </div>
              ))}
            </div>
            <p style={{ color: COLORS.textDim, fontSize: '12px', lineHeight: 1.5, marginBottom: '20px', fontStyle: 'italic' }}>
              {t('emergence.education.footer')}
            </p>
            <button
              onClick={handleEducationDone}
              style={{
                width: '100%', padding: '14px', background: `${COLORS.logos}20`,
                border: `1px solid ${COLORS.logos}`, borderRadius: '10px',
                color: COLORS.logos, fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {t('emergence.education.continue')}
            </button>
          </div>
        )}

        {/* ═══ HEADER (for select/detail steps) ═══ */}
        {(step === 'select' || step === 'detail') && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🌱</div>
            <h2 style={{ color: COLORS.text, fontSize: '18px', margin: 0, fontWeight: 600 }}>
              {t('emergence.title')}
            </h2>
            <p style={{ color: COLORS.textDim, fontSize: '13px', marginTop: '4px' }}>
              {t('emergence.ersLabel')}: <span style={{ color: ers > 0.5 ? COLORS.success : COLORS.warning }}>
                {ers.toFixed(3)}
              </span>
            </p>
          </div>
        )}

        {/* ═══ SELECT STEP ═══ */}
        {step === 'select' && (
          <>
            <p style={{ color: COLORS.textDim, fontSize: '14px', textAlign: 'center', marginBottom: '16px' }}>
              {t('emergence.question')}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              {categories.map(([key, cat]) => {
                const tCat = t(`emergence.categories.${key}`, { returnObjects: true });
                const catLabel = (typeof tCat === 'object' && tCat?.label) || cat.label;
                const catPrompt = (typeof tCat === 'object' && tCat?.prompt) || cat.prompt;
                return (
                  <button
                    key={key}
                    onClick={() => handleCategorySelect(key)}
                    style={{
                      background: COLORS.card, border: `1px solid ${COLORS.border}`,
                      borderRadius: '12px', padding: '14px 10px', cursor: 'pointer',
                      textAlign: 'center', transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = COLORS.cardHover;
                      e.currentTarget.style.borderColor = cat.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = COLORS.card;
                      e.currentTarget.style.borderColor = COLORS.border;
                    }}
                  >
                    <div style={{ fontSize: '28px', marginBottom: '6px' }}>{cat.emoji}</div>
                    <div style={{ color: cat.color, fontSize: '13px', fontWeight: 600 }}>{catLabel}</div>
                    <div style={{ color: COLORS.textDim, fontSize: '11px', marginTop: '4px', lineHeight: '1.3' }}>
                      {catPrompt}
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleSkip}
              style={{
                width: '100%', padding: '12px', background: 'transparent',
                border: `1px solid ${COLORS.border}`, borderRadius: '8px',
                color: COLORS.textDim, fontSize: '14px', cursor: 'pointer',
              }}
            >
              {t('emergence.skip')}
            </button>
          </>
        )}

        {/* ═══ DETAIL STEP ═══ */}
        {step === 'detail' && selectedCategory && (
          <>
            <div style={{
              background: COLORS.card, borderRadius: '12px', padding: '16px',
              textAlign: 'center', marginBottom: '16px',
              border: `1px solid ${EMERGENCE_TAXONOMY[selectedCategory].color}40`,
            }}>
              <span style={{ fontSize: '32px' }}>{EMERGENCE_TAXONOMY[selectedCategory].emoji}</span>
              <div style={{ color: EMERGENCE_TAXONOMY[selectedCategory].color, fontSize: '16px', fontWeight: 600, marginTop: '8px' }}>
                {t(`emergence.categories.${selectedCategory}.label`, EMERGENCE_TAXONOMY[selectedCategory].label)}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: COLORS.text, fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                {t('emergence.impact')}: <span style={{ color: COLORS.logos, fontWeight: 700 }}>{impact}/10</span>
              </label>
              <input
                type="range" min="1" max="10" value={impact}
                onChange={(e) => setImpact(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: COLORS.logos }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', color: COLORS.textDim, fontSize: '11px' }}>
                <span>{t('emergence.impactLow')}</span><span>{t('emergence.impactHigh')}</span>
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: COLORS.textDim, fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                {t('emergence.descLabel')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('emergence.descPlaceholder')}
                rows={2}
                style={{
                  width: '100%', background: COLORS.card, border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px', padding: '10px', color: COLORS.text, fontSize: '14px',
                  resize: 'none', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: COLORS.textDim, fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                {t('emergence.goalLabel')}
              </label>
              <input
                type="text"
                value={goalAssociation}
                onChange={(e) => setGoalAssociation(e.target.value)}
                placeholder={t('emergence.goalPlaceholder')}
                style={{
                  width: '100%', background: COLORS.card, border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px', padding: '10px', color: COLORS.text, fontSize: '14px',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setStep('select')}
                style={{
                  flex: 1, padding: '12px', background: COLORS.card,
                  border: `1px solid ${COLORS.border}`, borderRadius: '8px',
                  color: COLORS.textDim, fontSize: '14px', cursor: 'pointer',
                }}
              >
                {t('emergence.back')}
              </button>
              <button
                onClick={handleSubmit}
                style={{
                  flex: 2, padding: '12px', background: `${COLORS.logos}20`,
                  border: `1px solid ${COLORS.logos}`, borderRadius: '8px',
                  color: COLORS.logos, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('emergence.submit')}
              </button>
            </div>
          </>
        )}

        {/* ═══ CELEBRATION STEP (replaces old flat "done") ═══ */}
        {step === 'celebration' && selectedCategory && (
          <div style={{ textAlign: 'center', padding: '10px 0', position: 'relative' }}>
            <ConfettiParticles />
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div style={{
                fontSize: '56px', marginBottom: '12px',
                animation: 'celebration-pulse 1s ease-in-out infinite',
              }}>
                {EMERGENCE_TAXONOMY[selectedCategory]?.emoji || '✨'}
              </div>
              <h2 style={{ color: COLORS.gold, fontSize: '20px', margin: '0 0 8px', fontWeight: 700 }}>
                {t('emergence.done')}
              </h2>
              <p style={{ color: COLORS.logos, fontSize: '14px', fontWeight: 600, margin: '0 0 12px' }}>
                {t('emergence.celebration.eventCount', { count: eventCount })}
              </p>
              <div style={{
                background: `${COLORS.logos}15`, border: `1px solid ${COLORS.logos}30`,
                borderRadius: '12px', padding: '14px 16px', marginBottom: '16px',
              }}>
                <p style={{ color: COLORS.text, fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
                  {t(`emergence.celebration.${selectedCategory}`,
                    'Your emergence event has been recorded!')}
                </p>
              </div>
              <p style={{ color: COLORS.textDim, fontSize: '12px', fontStyle: 'italic', margin: '0 0 16px' }}>
                {t('emergence.celebration.keepGoing')}
              </p>
              <button
                onClick={onClose}
                style={{
                  padding: '12px 32px', background: `${COLORS.success}20`,
                  border: `1px solid ${COLORS.success}`, borderRadius: '10px',
                  color: COLORS.success, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                ✓
              </button>
            </div>
          </div>
        )}

        {/* ═══ DONE STEP (skip — no emergence) ═══ */}
        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✓</div>
            <p style={{ color: COLORS.success, fontSize: '16px', fontWeight: 600 }}>
              {t('emergence.done')}
            </p>
            <p style={{ color: COLORS.textDim, fontSize: '13px', marginTop: '4px' }}>
              {t('emergence.doneMessage')}
            </p>
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '12px', right: '12px',
            background: 'transparent', border: 'none', color: COLORS.textDim,
            fontSize: '20px', cursor: 'pointer', padding: '4px 8px',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
